/**
 * Deployer Reputation Store
 * 
 * Tracks the history of token creators to identify "Serial Deployers".
 * Persists to localStorage to build long-term reputation scores.
 */

export type DeployerHistory = {
  address: string;
  mints: string[];
  lastLaunchMs: number;
  totalVolumeSol: number;
  customName?: string;
  customColor?: string;
  isBlacklisted?: boolean;
  isAutoSnipe?: boolean;
  snipeAmountSol?: number;
  takeProfitPct?: number; // Target exit gain % (e.g. 50%)
  stopLossPct?: number;   // Stop loss % (e.g. 20%)
  exitOnDevSell?: boolean; // Emergency exit instantly on dev sell detection
};

const STORE_KEY = "solclaw_deployer_reputation_v1";

let internalStore: Map<string, DeployerHistory>;
try {
  const saved = localStorage.getItem(STORE_KEY);
  if (saved) {
    const parsed = JSON.parse(saved);
    internalStore = new Map(Object.entries(parsed));
  } else {
    internalStore = new Map();
  }
} catch {
  internalStore = new Map();
}

function persist() {
  try {
    // Cap to 1000 most recent to prevent localStorage QuotaExceededError
    if (internalStore.size > 1000) {
      const sorted = Array.from(internalStore.entries()).sort((a, b) => b[1].lastLaunchMs - a[1].lastLaunchMs);
      internalStore = new Map(sorted.slice(0, 1000));
    }
    const obj = Object.fromEntries(internalStore);
    localStorage.setItem(STORE_KEY, JSON.stringify(obj));
  } catch (err) {
    console.error("Failed to persist deployer store locally:", err);
    if (err instanceof DOMException && err.name === 'QuotaExceededError') {
      try {
        localStorage.removeItem(STORE_KEY);
        internalStore.clear();
      } catch (e) {
        // ignore
      }
    }
  }
}

// Background sync on load
fetch("/api/deployer-rules")
  .then(res => res.json())
  .then(rules => {
    rules.forEach((rule: DeployerHistory) => {
      internalStore.set(rule.address, { ...internalStore.get(rule.address), ...rule });
    });
    persist();
  })
  .catch(err => console.warn("Could not sync deployer rules from backend:", err));

export const deployerStore = {
  recordLaunch(address: string, mint: string) {
    const history = internalStore.get(address) || { address, mints: [], lastLaunchMs: 0, totalVolumeSol: 0 };
    if (!history.mints.includes(mint)) {
      history.mints.unshift(mint);
      history.mints = history.mints.slice(0, 50); // Keep last 50
    }
    history.lastLaunchMs = Date.now();
    internalStore.set(address, history);
    persist();
  },

  getHistory(address: string): DeployerHistory | undefined {
    return internalStore.get(address);
  },

  setRule(address: string, ruleUpdates: Partial<DeployerHistory>) {
    const history = internalStore.get(address) || { address, mints: [], lastLaunchMs: 0, totalVolumeSol: 0 };
    Object.assign(history, ruleUpdates);
    internalStore.set(address, history);
    persist();
    
    // Sync to backend
    fetch("/api/deployer-rules", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ address, rule: ruleUpdates })
    }).catch(err => console.warn("Failed to sync deployer rule to backend:", err));
  },

  getAllRules(): DeployerHistory[] {
    return Array.from(internalStore.values()).filter(h => h.customName || h.isBlacklisted || h.isAutoSnipe);
  },

  getReputation(address: string): { score: number; label: string; color: string } {
    const history = internalStore.get(address);
    if (!history) return { score: 100, label: "New Dev", color: "text-blue-400" };

    const launchCount = history.mints.length;
    
    // Very basic heuristic: many launches in a short time is a red flag for serial rugging
    if (launchCount > 5) return { score: 20, label: "Serial Deployer", color: "text-rose-500" };
    if (launchCount > 2) return { score: 60, label: "Active Dev", color: "text-amber-400" };
    
    return { score: 90, label: "Verified Dev", color: "text-emerald-400" };
  },

  getTag(address: string) {
    const history = internalStore.get(address);
    if (history?.isBlacklisted) {
      return { label: "Blacklisted", color: "text-red-500", isSerial: true, bg: "bg-red-500/20", isAutoSnipe: false };
    }
    if (history?.customName) {
      return { 
        label: history.customName, 
        color: history.customColor || "text-emerald-400", 
        isSerial: false,
        bg: history.customColor ? `bg-[${history.customColor.replace('text-', '')}]/20` : "bg-emerald-500/20",
        isAutoSnipe: history.isAutoSnipe
      };
    }

    const rep = this.getReputation(address);
    return {
      label: rep.label,
      color: rep.color,
      isSerial: rep.score <= 20,
      bg: "bg-black/40",
      isAutoSnipe: false
    };
  }
};
